// YouTube embed prompt for the article editor (window.prompt is banned).
// Accepts watch/short/embed URLs — the Youtube extension normalizes them.

import { useEffect, useState } from 'react'
import { isValidYoutubeUrl } from '@tiptap/extension-youtube'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog'

interface VideoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (url: string) => void
}

export function VideoDialog({ open, onOpenChange, onSubmit }: VideoDialogProps) {
  const [url, setUrl] = useState('')

  useEffect(() => { if (open) setUrl('') }, [open])

  const valid = isValidYoutubeUrl(url.trim())

  const submit = () => {
    if (!valid) return
    onSubmit(url.trim())
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle>Embed a video</DialogTitle>
          <DialogDescription>Paste a YouTube link (watch, share or embed URL).</DialogDescription>
        </DialogHeader>
        <form onSubmit={e => { e.preventDefault(); submit() }} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="video-url">YouTube URL</Label>
            <Input
              id="video-url"
              autoFocus
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
            />
            {url.trim() !== '' && !valid && (
              <p className="text-xs text-destructive">That doesn't look like a YouTube URL.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!valid}>Embed</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
